import React, { useEffect, useState } from "react";
import PageHeaderAlt from "components/layout-components/PageHeaderAlt";
import { Button, Form, message, Tabs } from "antd";
import Flex from "components/shared-components/Flex";
import GeneralField from "./GeneralField";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../data/hooks/firebase/useAuth";
import _, { uniq } from "lodash";
import { compressImage, getBase64 } from "functions/images";
import { uniqueMergeArrays } from "functions/arrays";
import { doc, setDoc, updateDoc } from "firebase/firestore";
import { firestore, storage } from "../../../../configs/FirebaseConfig";
import { getMetadata, ref } from "firebase/storage";
import { deleteFilesFromStorage, saveFilesToStorage } from "functions/firebase/storage";
import dayjs from "dayjs";
import { PRODUCT_IMAGE_COMPRESSION_QUALITY, PRODUCT_IMAGE_MAX_WIDTH } from "constants/ProductConstant";
import TranslationField from "./TranslationField";
import {
  FIRESTORE_PRICE_LISTS_TABLE,
  FIRESTORE_PRODUCTS_TABLE,
  FIRESTORE_REGIONS_TABLE,
} from "../../../../constants/FirestoreConstant";
import { COMPANY_COUNTRY_CODE } from "../../../../constants/CompanyConstants";
import { getProductTranslationAttributes, removeProductRedundantAttributes } from "../../../../functions/products";
import { useProducts } from "../../../../data/hooks/firestore/useProducts";
import Loading from "../../../../components/shared-components/Loading";
import { useExact } from "../../../../data/hooks/exact/useExact";
import { getUserPermissions } from "../../../../utils/permissionUtils";
import roles from "../../../../roles";
import { useCountries } from "../../../../data/hooks/firestore/useCountries";
import { usePriceLists } from "../../../../data/hooks/firestore/usePriceLists";
import { getCountryPriceForProduct } from "../../../../functions/pricing";
import { NETHERLANDS_COUNTRY_CODE } from "../../../../constants/PricingConstants";
import { NO_PACKAGING } from "./BundledProducts/PackagingMaterialSelector";

const { TabPane } = Tabs;

const ADD = "ADD";
const EDIT = "EDIT";
const imagesPath = "products";

const ProductForm = (props) => {
  const { t } = useTranslation();
  const { mode = ADD, param } = props;
  const [form] = Form.useForm();
  const [imageList, setImageList] = useState([]);
  const [originalImageList, setOriginalImageList] = useState([]);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewTitle, setPreviewTitle] = useState("");
  const [imageOrderChanged, setImageOrderChanged] = useState(false);
  const [newImagesUploaded, setNewImagesUploaded] = useState(false);
  const [showReorderImageNotification, setShowReorderImageNotification] = useState(false);
  const { email } = useAuth();
  const { products, isLoading: productsLoading } = useProducts();
  const categoriesNL = uniqueMergeArrays(products, "productCategoryNL");
  const categories = uniqueMergeArrays(products, "productCategory");
  const productId = !!props.param ? props.param.id : null;
  const clickedProduct = _.find(products, ["productId", productId]);
  const { products: exactProducts } = useExact();
  const [attributesEN, setAttributesEN] = useState(null);
  const [attributesNL, setAttributesNL] = useState(null);
  const [attributesDE, setAttributesDE] = useState(null);
  const [attributesFR, setAttributesFR] = useState(null);
  const { role: currentUserRole, uid } = useAuth();
  const [hasEditPermission, setHasEditPermission] = useState(true); // default true voor loading
  const { countries } = useCountries();
  const { priceLists } = usePriceLists();

  useEffect(() => {
    const checkPermissions = async () => {
      if (currentUserRole === roles.superAdmin) {
        setHasEditPermission(true);
      } else if (currentUserRole === roles.admin) {
        const permissions = await getUserPermissions(uid, currentUserRole);
        const canEdit =
          mode === "EDIT"
            ? permissions["shop-products-editProduct"]?.edit === true
            : permissions["shop-products-addProduct"]?.edit === true;
        setHasEditPermission(canEdit);
      }
    };
    checkPermissions();
  }, [currentUserRole, uid]);

  const isFieldDisabled = !hasEditPermission;

  useEffect(() => {
    if (mode === EDIT) {
      getStorageFiles().then((result) => {
        setImageList(result);
        setOriginalImageList(result);
      });
      form.setFieldsValue({
        productId: clickedProduct.productId,
        exactId: clickedProduct.exactId,
        name: clickedProduct.productName,
        shortDescription: clickedProduct.productDescription,
        description: clickedProduct.descriptionLong,
        category: clickedProduct.productCategory,
        price: clickedProduct.price || 0,
        shelfLife: clickedProduct.shelfLife || null,
        internalShelfLife: clickedProduct.internalShelfLife || null,
        size: clickedProduct.size,
        weight: clickedProduct.weight || null,
        barcode: clickedProduct.barcode || null,
        cratesPerLayer: clickedProduct.cratesPerLayer || null,
        productInStock: clickedProduct.productInStock,
        availableIn: clickedProduct.availableIn,
        productInSeason: clickedProduct.productInSeason,
        attributes: !!clickedProduct.productAttributes ? clickedProduct.productAttributes : [],
        isVisible: typeof clickedProduct.isVisible !== "undefined" ? clickedProduct.isVisible : true,
        productGroup: clickedProduct.productGroup,
        company: clickedProduct.company,
        updatedBy: clickedProduct.updatedBy,
        updatedAt: clickedProduct.updatedAt,
        // Translations
        name_nl: !!clickedProduct.name ? clickedProduct.name.nl : "",
        name_de: !!clickedProduct.name ? clickedProduct.name.de : null,
        name_fr: !!clickedProduct.name ? clickedProduct.name.fr : null,
        description_nl: !!clickedProduct.description ? clickedProduct.description.nl : "",
        description_de: !!clickedProduct.description ? clickedProduct.description.de : null,
        description_fr: !!clickedProduct.description ? clickedProduct.description.fr : null,
        shortDescription_nl: !!clickedProduct.shortDescription ? clickedProduct.shortDescription.nl : "",
        shortDescription_de: !!clickedProduct.shortDescription ? clickedProduct.shortDescription.de : null,
        shortDescription_fr: !!clickedProduct.shortDescription ? clickedProduct.shortDescription.fr : null,
        category_nl: !!clickedProduct.category ? clickedProduct.category.nl : "",
        category_de: !!clickedProduct.category ? clickedProduct.category.de : null,
        category_fr: !!clickedProduct.category ? clickedProduct.category.fr : null,
        attributes_nl: !!clickedProduct.attributes ? clickedProduct.attributes.nl : [],
        attributes_de: !!clickedProduct.attributes ? clickedProduct.attributes.de : [],
        attributes_fr: !!clickedProduct.attributes ? clickedProduct.attributes.fr : [],

        // Bundle fields:
        isBundle: clickedProduct.isBundle || false,
        bundleItems: clickedProduct.bundleItems || [],
        packagingMaterial:
          clickedProduct.packagingMaterial ||
          (clickedProduct.isBundle ? { productId: NO_PACKAGING, quantity: 1 } : null),
        hasCustomPrice: clickedProduct.hasCustomPrice || false,
      });

      // Dynamically set VAT fields for all countries
      if (countries.length > 0 && clickedProduct.vat) {
        const vatFields = {};
        countries.forEach((country) => {
          const countryCode = country.countryCode.toLowerCase();
          vatFields[`vat_${countryCode}`] =
            clickedProduct.vat[country.countryCode.toLowerCase()] !== null &&
            clickedProduct.vat[country.countryCode.toLowerCase()] !== undefined
              ? clickedProduct.vat[country.countryCode.toLowerCase()]
              : null;
        });
        form.setFieldsValue(vatFields);
      }

      // Dynamically set country price override fields from country price lists.
      // NL has no override field - its price IS the standard price field above.
      if (countries.length > 0) {
        const countryPriceFields = {};
        countries
          .filter((country) => country.countryCode !== NETHERLANDS_COUNTRY_CODE)
          .forEach((country) => {
            const countryCode = country.countryCode.toLowerCase();
            const countryPrice = getCountryPriceForProduct(clickedProduct, country.countryCode, priceLists);
            countryPriceFields[`countryPrice_${countryCode}`] = countryPrice !== null ? parseFloat(countryPrice) : null;
          });
        form.setFieldsValue(countryPriceFields);
      }
    }
  }, [form, mode, param, props, clickedProduct, countries, priceLists]);

  useEffect(() => {
    if (!!products && products.length > 0) {
      const en = [];
      const nl = [];
      const de = [];
      const fr = [];

      products.forEach((product) => {
        if (!!product.attributes) {
          // Add all array items to the attributes array
          if (!!product.attributes.en) en.push(...product.attributes.en);
          if (!!product.attributes.nl) nl.push(...product.attributes.nl);
          if (!!product.attributes.de) de.push(...product.attributes.de);
          if (!!product.attributes.fr) fr.push(...product.attributes.fr);
        }
      });
      // Sort alphabetically and remove duplicates
      setAttributesEN(uniq(en).sort());
      setAttributesNL(uniq(nl).sort());
      setAttributesDE(uniq(de).sort());
      setAttributesFR(uniq(fr).sort());
    }
  }, [products]);

  /**
   * Retrieve files from firebase storage with their links
   * @returns Array of images
   */
  function getStorageFiles() {
    let promises = [];
    if (!!clickedProduct && !!clickedProduct.productImage512DownloadURL) {
      clickedProduct.productImage512DownloadURL.forEach((imageUrl, index) => {
        let imageRef = ref(storage, imageUrl);
        promises.push(
          getMetadata(imageRef)
            .then((info) => {
              return {
                uid: info.name,
                url: imageUrl,
                name: info.name,
                path: info.fullPath,
              };
            })
            .catch(() => {
              // Image does not exist in storage anymore
              return {};
            }),
        );
      });
    }
    return Promise.all(promises);
  }

  const handleUploadChange = ({ fileList }) => {
    let uploadedFile = fileList[fileList.length - 1];
    if (!!uploadedFile && uploadedFile.status === "done") {
      setNewImagesUploaded(true);
      // Compress image before setting imageList state
      compressImage(uploadedFile.originFileObj, PRODUCT_IMAGE_COMPRESSION_QUALITY, PRODUCT_IMAGE_MAX_WIDTH).then(
        (compressedImage) => {
          let compressedImageClone = new Blob([compressedImage], {
            type: compressedImage.type,
          });
          compressedImageClone.name = compressedImage.lastModified + "-" + compressedImage.name;
          // Clone object, it may have some read-only properties
          fileList[fileList.length - 1] = {
            ...fileList[fileList.length - 1],
            originFileObj: compressedImageClone,
            name: compressedImage.lastModified + "-" + compressedImage.name,
            size: compressedImage.size,
          };
          setImageList(fileList);
        },
      );
    } else setImageList(fileList);
  };

  const handleUploadOrderChange = (fileList) => {
    setImageList(fileList);
    setImageOrderChanged(true);
    if (newImagesUploaded) setShowReorderImageNotification(true);
  };

  const handlePreviewCancel = () => {
    setPreviewVisible(false);
  };

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }

    setPreviewImage(file.url || file.preview);
    setPreviewVisible(true);
    setPreviewTitle(file.name || file.url.substring(file.url.lastIndexOf("/") + 1));
  };

  const setProductLoggingProperties = (product, mode) => {
    if (mode === ADD) return { ...product, createdAt: dayjs().unix(), createdBy: email };
    else if (mode === EDIT) return { ...product, updatedAt: dayjs().unix(), updatedBy: email };
  };

  /**
   * Extract the per-country price override fields (countryPrice_<code>) from the raw form values
   * and strip them off, since they must not be persisted as literal fields on the product document.
   * NL has no override field - its price is the standard price field (newProduct.price) itself.
   * @param {object} newProduct - Raw form values (mutated: countryPrice_<code> keys are removed)
   * @returns {object} Map of countryCode -> override price (only for countries with a value)
   */
  const extractCountryPriceOverrides = (newProduct) => {
    const overrides = {};
    countries
      .filter((country) => country.countryCode !== NETHERLANDS_COUNTRY_CODE)
      .forEach((country) => {
        const fieldName = `countryPrice_${country.countryCode.toLowerCase()}`;
        const value = newProduct[fieldName];
        if (value !== null && value !== undefined && value !== "") {
          overrides[country.countryCode] = parseFloat(value);
        }
        delete newProduct[fieldName];
      });
    return overrides;
  };

  /**
   * Write per-country price overrides to the corresponding country price lists. The product form
   * never creates a country price list itself - it only writes into lists that already exist
   * (created via the Price Lists page). NL is always written (even when equal to the standard
   * price) as the explicit product/price-list dual-write during the migration period; other
   * countries only get an entry when they actually have an override AND already have a list.
   * Failures are caught per-country so a price-list write never blocks the product save itself.
   * @param {object} countryPriceOverrides - Map of countryCode -> override price
   * @param {string} productDocId - Firestore document id of the product
   * @param {number} standardPrice - The product's standard price (used to decide "no override")
   */
  const saveCountryPrices = async (countryPriceOverrides, productDocId, standardPrice) => {
    // Also revisit countries that currently have a stored entry for this product but no override
    // anymore (e.g. the user cleared the field) - otherwise a cleared override never gets removed.
    const countriesWithExistingEntry = priceLists
      .filter((pl) => pl.type === "country" && pl.customPrices?.[productDocId] !== undefined)
      .map((pl) => pl.countryCode);

    const countryCodesToWrite = uniq([
      NETHERLANDS_COUNTRY_CODE,
      ...Object.keys(countryPriceOverrides),
      ...countriesWithExistingEntry,
    ]);

    for (const countryCode of countryCodesToWrite) {
      try {
        const overridePrice = countryPriceOverrides[countryCode];
        const isNetherlands = countryCode === NETHERLANDS_COUNTRY_CODE;
        // NL is always kept in sync with the product price during the migration period, even without an override
        const priceToStore = overridePrice !== undefined ? overridePrice : isNetherlands ? standardPrice : undefined;

        const countryPriceList = priceLists.find((pl) => pl.type === "country" && pl.countryCode === countryCode);

        // No existing list for this country - nothing to write, the form never auto-creates one
        if (!countryPriceList) continue;

        const isOverrideEqualToStandard =
          priceToStore !== undefined && priceToStore === standardPrice && !isNetherlands;
        const updatedCustomPrices = { ...countryPriceList.customPrices };

        if (priceToStore === undefined || isOverrideEqualToStandard) {
          delete updatedCustomPrices[productDocId];
        } else {
          updatedCustomPrices[productDocId] = priceToStore;
        }

        await updateDoc(
          doc(
            firestore,
            FIRESTORE_REGIONS_TABLE,
            COMPANY_COUNTRY_CODE,
            FIRESTORE_PRICE_LISTS_TABLE,
            countryPriceList.id,
          ),
          {
            customPrices: updatedCustomPrices,
            updatedAt: dayjs().unix(),
            updatedBy: email,
          },
        );
      } catch (err) {
        console.error(`[ProductForm] Error saving country price list for ${countryCode}`, err);
        message.error(t("notifications.error_saving_country_price", { country: countryCode }));
      }
    }
  };

  const onFinish = () => {
    form
      .validateFields()
      .then(async (newProduct) => {
        setSubmitLoading(true);

        // Validate and set the price field (deprecated: productPrice no longer written)
        const priceValue = parseFloat(newProduct.price);
        if (isNaN(priceValue) || priceValue < 0) {
          message.error(t("form.invalid_price"));
          setSubmitLoading(false);
          return;
        }

        newProduct.price = parseFloat(priceValue.toFixed(2));

        // Extract per-country price overrides before they get stripped from the product payload
        const countryPriceOverrides = extractCountryPriceOverrides(newProduct);

        // Validate bundle-specific requirements
        if (newProduct.isBundle) {
          if (!newProduct.bundleItems || newProduct.bundleItems.length === 0) {
            message.error(t("notifications.bundle_needs_items"));
            setSubmitLoading(false);
            return;
          }

          if (!newProduct.packagingMaterial?.productId) {
            message.error(t("notifications.bundle_needs_packaging"));
            setSubmitLoading(false);
            return;
          }

          // Check if any bundle item has null productId
          const hasInvalidItems = newProduct.bundleItems.some((item) => !item.productId);
          if (hasInvalidItems) {
            message.error(t("notifications.bundle_has_empty_items"));
            setSubmitLoading(false);
            return;
          }
        }

        const firestorePath = `${FIRESTORE_REGIONS_TABLE}/${COMPANY_COUNTRY_CODE}/${FIRESTORE_PRODUCTS_TABLE}`;
        const id = newProduct.productId;
        newProduct.availableIn = newProduct.availableIn || null;
        newProduct.shelfLife = newProduct.shelfLife || null;
        newProduct.internalShelfLife = newProduct.internalShelfLife || null;
        newProduct.weight = newProduct.weight || null;
        newProduct.barcode = newProduct.barcode || null;
        newProduct.cratesPerLayer = newProduct.cratesPerLayer || null;
        newProduct.productImage512StoragePaths = [];
        newProduct.productImageStoragePaths = [];
        newProduct.productLikes = [];
        newProduct.productNameNL = newProduct.name_nl || newProduct.name;
        newProduct.productCategoryNL = newProduct.category_nl || newProduct.category;
        newProduct.productAttributesNL = newProduct.attributes_nl || [];
        newProduct.descriptionLongNL = newProduct.description_nl || newProduct.description;
        newProduct.productDescriptionNL = newProduct.shortDescription_nl || newProduct.shortDescription;

        const foundExactProduct = _.find(exactProducts, ["ID", newProduct.exactId]);
        if (!!foundExactProduct) {
          newProduct.exactItemCode = foundExactProduct.ItemCode;
        }
        if (imageList.length === 0) newProduct.productImage512DownloadURL = [];

        // Bundle fields handling
        if (newProduct.isBundle) {
          const calculatedItemPrices = newProduct.calculatedItemPrices || {};

          // Map bundleItems with calculated itemPrice
          newProduct.bundleItems = (newProduct.bundleItems || []).map((item) => {
            const calculatedPrice = calculatedItemPrices[item.productId];
            const itemPrice = calculatedPrice !== undefined ? calculatedPrice : 0;
            return {
              productId: item.productId,
              quantity: item.quantity,
              price: parseFloat(itemPrice.toFixed(2)),
            };
          });

          // Add price to packagingMaterial
          if (newProduct.packagingMaterial?.productId === NO_PACKAGING) {
            newProduct.packagingMaterial = null;
          } else if (newProduct.packagingMaterial?.productId) {
            const packagingProduct = products.find((p) => p.productId === newProduct.packagingMaterial.productId);
            const packagingPrice = packagingProduct ? packagingProduct.price || 0 : 0;
            newProduct.packagingMaterial = {
              productId: newProduct.packagingMaterial.productId,
              quantity: 1,
              price: parseFloat(packagingPrice.toFixed(2)),
            };
          }

          newProduct.hasCustomPrice = newProduct.hasCustomPrice || false;

          // Set fields that bundles don't use to null/empty
          newProduct.weight = null;
          newProduct.productAttributesNL = [];
          newProduct.productCategoryNL = null;
          newProduct.category = null;
          newProduct.attributes = null;

          // Dynamically set all VAT fields to null for all countries
          countries.forEach((country) => {
            newProduct[`vat_${country.countryCode.toLowerCase()}`] = null;
          });

          newProduct.size = null;

          // Ensure productGroup is set to bundles
          newProduct.productGroup = "Bundles";

          // Remove the calculatedItemPrices field before saving
          delete newProduct.calculatedItemPrices;
        } else {
          // Not a bundle - clear bundle fields
          newProduct.isBundle = false;
          newProduct.bundleItems = [];
          newProduct.packagingMaterial = null;
          newProduct.hasCustomPrice = false;
          delete newProduct.calculatedItemPrices;
        }

        // Translations
        const translations = getProductTranslationAttributes(newProduct, countries);
        newProduct = { ...newProduct, ...translations };
        newProduct = await removeProductRedundantAttributes(newProduct);
        let productWithLogging = setProductLoggingProperties(newProduct, mode);

        /** ADD PRODUCT */
        if (mode === ADD) {
          let imageUrlArray = [];
          let fileArray = [];
          // Push images to array, we will add those to product image array.
          imageList.forEach((item) => {
            fileArray.push(item.originFileObj);
          });

          try {
            console.groupCollapsed("[ProductForm] Saving product (ADD)");
            console.log("mode", mode);
            console.log("id", id);
            console.log("firestorePath", firestorePath);
            console.log("newProduct (after transforms)", newProduct);
            console.log("productWithLogging (payload)", productWithLogging);
            console.log(
              "imageList",
              imageList?.map((i) => ({
                name: i?.name,
                uid: i?.uid,
                status: i?.status,
                hasUrl: !!i?.url,
                url: i?.url,
                hasOriginFileObj: !!i?.originFileObj,
                originName: i?.originFileObj?.name,
                originType: i?.originFileObj?.type,
                originSize: i?.originFileObj?.size,
              })),
            );
            console.groupEnd();

            // Update the product with the new form data
            await setDoc(
              doc(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_PRODUCTS_TABLE, id),
              productWithLogging,
            );

            // Sync price to country price lists (NL always, other countries only when overridden)
            await saveCountryPrices(countryPriceOverrides, id, priceValue);

            // Save images to storage and then add the download urls to the product
            if (fileArray.length > 0) {
              await saveFilesToStorage(imagesPath, fileArray, imageUrlArray, id, email);
            }

            form.resetFields();
            setImageList([]);
            message.success(t("product_added"), 4);
          } catch (err) {
            message.error(err.message);
          }
        }
        /** EDIT PRODUCT */
        if (mode === EDIT) {
          let newFiles = [];
          let imageUrlArray = [];

          // Push existing files to imageUrlArray, we do not need to re-upload those.
          // Push new files to newFiles array, we will add those to product image array.
          imageList.forEach((item) => {
            if (!!item.url) imageUrlArray.push(item.url);
            else newFiles.push(item.originFileObj);
          });

          // Go over the original image list and check which ones are removed. Put those in corresponding arrays
          let toDeleteFromStorage = [];
          let toDeleteFromFirestore = [];
          _.forEach(originalImageList, function (value) {
            if (value.status === "removed") {
              toDeleteFromStorage.push(value.path);
              toDeleteFromFirestore.push(value.url);
            }
          });

          let imageUrls = [];
          // Save new files to storage if added
          try {
            console.groupCollapsed("[ProductForm] Preparing product update (EDIT)");
            console.log("mode", mode);
            console.log("productId", productId);
            console.log("productWithLogging (pre-save)", productWithLogging);
            console.log(
              "imageList",
              imageList?.map((i) => ({
                name: i?.name,
                uid: i?.uid,
                status: i?.status,
                hasUrl: !!i?.url,
                url: i?.url,
                hasOriginFileObj: !!i?.originFileObj,
                originName: i?.originFileObj?.name,
                originType: i?.originFileObj?.type,
                originSize: i?.originFileObj?.size,
              })),
            );
            console.log(
              "newFiles",
              newFiles?.map((f) => ({
                name: f?.name,
                type: f?.type,
                size: f?.size,
              })),
            );
            console.log("imageUrlArray (existing urls)", imageUrlArray);
            console.log("imageOrderChanged", imageOrderChanged);
            console.groupEnd();

            if (newFiles.length > 0) {
              await saveFilesToStorage(imagesPath, newFiles, imageUrlArray, productId, email);
              updateProduct(productWithLogging);
            } else if (imageOrderChanged) {
              imageList.forEach((image) => {
                let foundImage = _.find(imageList, (item) => item.url === image.url);
                if (!!foundImage) {
                  imageUrls.push(image.url);
                }
              });
              productWithLogging.productImage512DownloadURL = imageUrls;
              updateProduct(productWithLogging);
            } else {
              updateProduct(productWithLogging);
            }
          } catch (err) {
            console.error("[ProductForm] Error while preparing product update (EDIT)", err);
            message.error(err.message);
          }

          // Delete files from storage if removed
          if (toDeleteFromStorage.length > 0) {
            await deleteFilesFromStorage(toDeleteFromStorage, productId, toDeleteFromFirestore, email);
          }

          async function updateProduct(updatedProduct) {
            try {
              console.groupCollapsed("[ProductForm] Saving product (EDIT)");
              console.log("mode", mode);
              console.log("productId", productId);
              console.log("firestorePath", firestorePath);
              console.log("updatedProduct (payload)", updatedProduct);
              console.log(
                "imageList",
                imageList?.map((i) => ({
                  name: i?.name,
                  uid: i?.uid,
                  status: i?.status,
                  hasUrl: !!i?.url,
                  url: i?.url,
                  hasOriginFileObj: !!i?.originFileObj,
                  originName: i?.originFileObj?.name,
                  originType: i?.originFileObj?.type,
                  originSize: i?.originFileObj?.size,
                })),
              );
              console.groupEnd();

              // Update the product with the new form data
              await updateDoc(
                doc(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_PRODUCTS_TABLE, productId),
                updatedProduct,
              );

              // Sync price to country price lists (NL always, other countries only when overridden)
              await saveCountryPrices(countryPriceOverrides, productId, priceValue);

              message.success(t("product_saved"), 4);
              setOriginalImageList(imageList);
              setShowReorderImageNotification(false);
              setNewImagesUploaded(false);
            } catch (err) {
              message.error(err.message);
            }
          }
        }
        setSubmitLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setSubmitLoading(false);
        message.error(t("enter_required_data"));
      });
  };

  if (productsLoading) {
    return <Loading />;
  }

  return (
    <>
      <Form
        layout="vertical"
        form={form}
        name="advanced_search"
        className="ant-advanced-search-form"
        initialValues={{
          discount: 0,
          stock: 0,
          min_amount: 1,
          productInStock: true,
          productInSeason: true,
          productInSnelstart: true,
          isVisible: true,
          isBundle: false,
          bundleItems: [],
          packagingMaterial: null,
          hasCustomPrice: false,
        }}
      >
        <PageHeaderAlt className="border-bottom" overlap>
          <div className="container">
            <Flex className="py-2" mobileFlex={false} justifyContent="between" alignItems="center">
              <h2 className="mb-3">{mode === "ADD" ? t("add_new_product") : t("edit_product")} </h2>
              <div className="mb-3">
                <Button
                  type="primary"
                  onClick={() => onFinish()}
                  htmlType="submit"
                  loading={submitLoading}
                  disabled={isFieldDisabled}
                >
                  {mode === "ADD" ? t("add") : t("save")}
                </Button>
              </div>
            </Flex>
          </div>
        </PageHeaderAlt>
        <div className="container">
          <Tabs defaultActiveKey="1" style={{ marginTop: 30 }}>
            <TabPane tab={t("general")} key="1">
              <GeneralField
                form={form}
                products={products}
                fileList={imageList}
                handleUploadChange={handleUploadChange}
                handleUploadOrderChange={handleUploadOrderChange}
                categories={categories}
                productInStock={clickedProduct?.productInStock}
                availableIn={clickedProduct?.availableIn}
                attributes={attributesEN}
                handlePreview={handlePreview}
                previewVisible={previewVisible}
                previewImage={previewImage}
                previewTitle={previewTitle}
                handlePreviewCancel={handlePreviewCancel}
                productId={productId}
                showReorderNotification={showReorderImageNotification}
                mode={mode}
                exactProducts={exactProducts}
                updatedBy={clickedProduct?.updatedBy}
                updatedAt={clickedProduct?.updatedAt}
                isFieldDisabled={isFieldDisabled}
              />
            </TabPane>
            <TabPane tab={t("translations")} key="2" forceRender>
              <TranslationField
                form={form}
                products={products}
                categories={categories}
                previewTitle={previewTitle}
                productId={productId}
                attributesNL={attributesNL}
                attributesDE={attributesDE}
                attributesFR={attributesFR}
                isFieldDisabled={isFieldDisabled}
              />
            </TabPane>
          </Tabs>
        </div>
      </Form>
    </>
  );
};
export default ProductForm;
